import { useState } from "react";
import axios from "axios";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel } from "@/components/admin/ui";

export default function OrgSolutionCapabilitiesPanel({ org, solution, API, onSaved }) {
  const [checked, setChecked] = useState(new Set(solution.capabilities.filter((c) => c.enabled).map((c) => c.capability_id)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = (capabilityId) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(capabilityId) ? next.delete(capabilityId) : next.add(capabilityId);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      await axios.patch(
        `${API}/admin/organizations/${org.org_id}/solutions/${solution.solution_id}/capabilities`,
        { capability_ids: Array.from(checked) },
        { withCredentials: true }
      );
      await onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Panel
      title={`${solution.name} — Capabilities`}
      subtitle={`Select which ${solution.name.toLowerCase()} capabilities are available to this organization.`}
      action={
        <span className={`text-xs font-bold ${solution.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500"}`} data-testid={`org-solution-capabilities-enabled-${solution.slug}`}>
          {solution.enabled ? "Enabled" : "Disabled"}
        </span>
      }
      testId={`org-solution-capabilities-panel-${solution.slug}`}
    >
      {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400">{error}</p>}
      <div className="grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3" data-testid={`org-solution-capabilities-list-${solution.slug}`}>
        {solution.capabilities.map((c) => (
          <label key={c.capability_id} className="flex items-start gap-2.5 rounded-xl border border-slate-200 p-3 text-sm hover:border-slate-300 dark:border-white/10 dark:hover:border-white/20" data-testid={`org-capability-row-${solution.slug}-${c.slug}`}>
            <input
              type="checkbox"
              checked={checked.has(c.capability_id)}
              onChange={() => toggle(c.capability_id)}
              data-testid={`org-capability-checkbox-${solution.slug}-${c.slug}`}
              className="mt-0.5"
            />
            <span className="font-semibold text-slate-800 dark:text-slate-200">{c.name}</span>
          </label>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-4">
        <button type="button" onClick={save} disabled={saving} data-testid={`org-solution-capabilities-save-${solution.slug}`} className="inline-flex items-center gap-2 rounded-xl bg-[#0052FF] px-5 py-2.5 text-sm font-bold text-white transition-colors duration-200 hover:bg-blue-700 disabled:opacity-50">
          {saving ? "Saving…" : "Save Capabilities"}
        </button>
        {solution.customized && <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">Adjusted from default recommendations</span>}
      </div>
    </Panel>
  );
}
